import { SpinnerMark } from "@workspace/ui/components/atoms/spinner"
import { ChangeEvent, useEffect, useState } from "react"
import { useForm, FormProvider, SubmitHandler, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { confirmResetPassword } from "aws-amplify/auth"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { cn } from "@dashboard/lib/utils"
import { useNavigate, useSearchParams } from "react-router-dom"
import PasswordLabel from "../../components/label";

const passwordResetSchema = z
	.object({
		code: z.string().min(6, "Code must be exactly 6 digits"),
		newPassword: z
			.string()
			.min(11, "Password must be at least 11 characters")
			.regex(/[A-Z]/, "Password must include at least one uppercase letter")
			.regex(/[a-z]/, "Password must include at least one lowercase letter")
			.regex(/[0-9]/, "Password must include at least one number")
			.regex(
				/[^A-Za-z0-9]/,
				"Password must include at least one special character",
			)
			.refine((value) => value.trim() === value, {
				message: "Password must not have leading or trailing spaces",
			}),
		confirmPassword: z.string().min(1, "Confirm Password is required"),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "Password and Confirm Password do not match",
		path: ["confirmPassword"],
	})

type PasswordResetFormValues = z.infer<typeof passwordResetSchema>

export const ConfirmPasswordForm = ({
	email,
	tokenCode,
}: {
	email: string
	tokenCode: string | null
}) => {
	const navigate = useNavigate()
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const [searchParams] = useSearchParams()
	const emailJwtCode = searchParams.get("code")

	const form = useForm<PasswordResetFormValues>({
		resolver: zodResolver(passwordResetSchema),
	})

	const newPassword = useWatch({ control: form.control, name: "newPassword" })
	const confirmPassword = useWatch({
		control: form.control,
		name: "confirmPassword",
	})

	useEffect(() => {
		if (confirmPassword) {
			form.trigger("confirmPassword")
		}
	}, [newPassword, confirmPassword, form])

	const onSubmit: SubmitHandler<PasswordResetFormValues> = async ({
		code,
		newPassword,
	}) => {
		try {
			await confirmResetPassword({
				username: email,
				confirmationCode: code,
				newPassword,
			})
			setShowSuccess(true)
		} catch (error: any) {
			if (error.name === "CodeMismatchException") {
				form.setError("code", {
					type: "custom",
					message: "Invalid verification code.",
				})
			} else if (error.name === "ExpiredCodeException") {
				form.setError("code", {
					type: "custom",
					message: "Code expired. Request a new one.",
				})
			} else if (error.name === "InvalidPasswordException") {
				form.setError("newPassword", {
					type: "custom",
					message: "Invalid password format.",
				})
			}
		}
	}

	return (
		<>
			{showSuccess && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md">
					<div className="w-90 rounded-lg bg-white p-6 text-center shadow-lg">
						<h2 className="mb-2 text-lg font-semibold text-green-700">
							Success!
						</h2>
						<p className="mb-4 text-gray-800">
							Password {emailJwtCode ? "created" : "reset"} successfully. Please
							login.
						</p>
						<button
							onClick={() => navigate("/login")}
							className="mt-2 w-full cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
						>
							OK
						</button>
					</div>
				</div>
			)}

			<FormProvider {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-4"
					autoComplete="off"
				>
					<div className="text-center text-sm text-gray-700">
						Please enter the verification code sent to your
						<div> email and set your new password.</div>
					</div>
					<label className="flex justify-start text-[14px] font-medium leading-5 text-gray-800">
						Verification Code
					</label>
					<input
						type="text"
						placeholder="Enter Code"
						maxLength={6}
						autoComplete="off"
						{...form.register("code", {
							onChange: (e: ChangeEvent<HTMLInputElement>) => {
								const numericOnly = e.target.value?.replace(/\D/g, "")
								form.setValue("code", numericOnly)
							},
						})}
						className="mb-1 mt-1 w-full rounded-md border border-[#E5E7EB] px-4 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
					{form.formState.errors.code && (
						<p className="text-sm text-red-500">
							{form.formState.errors.code.message}
						</p>
					)}
					<PasswordLabel />
					<div className="relative w-full">
						<input
							type={showPassword ? "text" : "password"}
							placeholder="Password"
							autoComplete="off"
							{...form.register("newPassword")}
							className="mt-1 w-full rounded-md border border-[#E5E7EB] px-4 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
						<button
							type="button"
							className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-500"
							onClick={() => setShowPassword(!showPassword)}
							tabIndex={-1}
						>
							{showPassword ? <FaEye /> : <FaEyeSlash />}
						</button>
					</div>
					{form.formState.errors.newPassword && (
						<p className="text-sm text-red-500">
							{form.formState.errors.newPassword.message}
						</p>
					)}

					<div className="relative mb-4 w-full">
						<input
							type={showConfirmPassword ? "text" : "password"}
							placeholder="Confirm Password"
							autoComplete="off"
							{...form.register("confirmPassword")}
							className="mt-1 w-full rounded-md border border-[#E5E7EB] px-4 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
						<button
							type="button"
							className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-500"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							tabIndex={-1}
						>
							{showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
						</button>
						{form.formState.errors.confirmPassword && (
							<p className="text-sm text-red-500">
								{form.formState.errors.confirmPassword.message}
							</p>
						)}
					</div>

					<button
						type="submit"
						disabled={form.formState.isSubmitting}
						className={cn(
							// NOTE: no `btn` class here on purpose — the dashboard's global
							// `body:not(.dashboard-role-active) .btn` rule (globals.css) forces a
							// white bg + dark text and would override bg-[#0066FF]/text-white,
							// making this CTA colorless. Omitting `btn` matches the in-game and
							// EmailVerificationForm buttons exactly (blue #0066FF fill).
							"mt-4 flex w-full cursor-pointer items-center justify-center rounded-md bg-[#0066FF] py-3 text-sm font-semibold text-white transition hover:bg-[#0056d6] sm:py-2 sm:text-base",
							{
								"cursor-not-allowed bg-[#3A72C4]": form.formState.isSubmitting,
							},
						)}
					>
						{tokenCode ? (
							<span className="flex items-center justify-center gap-2">
								{form.formState.isSubmitting ? (
									<>
										<SpinnerMark size={16} color="#ffffff" />
										Creating...
									</>
								) : (
									"Create Password"
								)}
							</span>
						) : (
							<span className="flex items-center justify-center gap-2">
								{form.formState.isSubmitting ? (
									<>
										<SpinnerMark size={16} color="#ffffff" />
										Resetting...
									</>
								) : (
									"Reset Password"
								)}
							</span>
						)}
					</button>
				</form>
			</FormProvider>
		</>
	)
}
